/**
 * Motor "sequenciar" — ordenar cartoes de passos.
 *
 * Dois modos:
 *   "montar"  — os cartoes vem embaralhados e a crianca poe na ordem
 *   "depurar" — a sequencia ja vem montada, mas com N passos trocados de lugar.
 *               A crianca acha o que esta fora do lugar e conserta. E o primeiro
 *               contato com depuracao: o programa existe, ele so esta errado.
 *
 * D6: cartao no lugar trocado nao e erro. Da para remexer a vontade.
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';

export class SequenciarEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.lugares = [];
    this.cartoes = [];
    this.grupo = null;
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(`FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}.`);
    }

    this.quantidade = payload.passos;
    this.modo = payload.modo; // "montar" | "depurar"
    this.errosPlantados = payload.erros ?? 0;

    if (this.modo === 'depurar' && this.errosPlantados < 1) {
      throw new Error(
        `FAZENDA: a tarefa "${tarefa.id}" esta no modo "depurar" na banda ${banda} mas nao diz ` +
          'quantos "erros" plantar. Sem erro plantado nao ha nada para consertar.'
      );
    }

    this.montar();
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ` +
        `${this.quantidade} passos, modo "${this.modo}".`
    );
  }

  montar() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-sequenciar';
    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);
    const largura = (this.quantidade - 1) * 1.5;

    for (let i = 0; i < this.quantidade; i++) {
      const lugar = new THREE.Mesh(
        new THREE.BoxGeometry(1.25, 0.08, 1.6),
        new THREE.MeshStandardMaterial({
          color: 0x795548, roughness: 1, transparent: true, opacity: 0.6, flatShading: true,
        })
      );
      lugar.position.set(base.x - largura / 2 + i * 1.5, base.y + 0.05, base.z + 2.4);
      lugar.userData.passo = i;
      lugar.userData.ocupadoPor = null;
      this.grupo.add(lugar);
      this.lugares.push(lugar);
    }

    // Cada cartao ganha uma cor da escala, para a ordem ser visivel sem ler.
    const ordemInicial = this.modo === 'depurar'
      ? comErrosPlantados(this.quantidade, this.errosPlantados)
      : embaralhar([...Array(this.quantidade).keys()]);

    ordemInicial.forEach((passo, posicao) => {
      const matiz = passo / Math.max(1, this.quantidade - 1);
      const cartao = new THREE.Mesh(
        new THREE.BoxGeometry(1.15, 0.16, 1.5),
        new THREE.MeshStandardMaterial({
          color: new THREE.Color().setHSL(0.08 + matiz * 0.42, 0.62, 0.58),
          roughness: 0.95,
          flatShading: true,
        })
      );

      if (this.modo === 'depurar') {
        // Ja nasce encaixado, na ordem (com erros).
        const lugar = this.lugares[posicao];
        cartao.position.set(lugar.position.x, lugar.position.y + 0.13, lugar.position.z);
        lugar.userData.ocupadoPor = cartao;
        cartao.userData.noLugar = lugar;
      } else {
        cartao.position.set(base.x - largura / 2 + posicao * 1.5, base.y + 0.18, base.z - 0.6);
        cartao.userData.noLugar = null;
      }

      cartao.userData.passo = passo;
      this.grupo.add(cartao);
      this.cartoes.push(cartao);

      input.registrarArrastavel(cartao, {
        destinos: () => this.lugares,
        aoSoltar: (objeto, lugar) => this.aoSoltarCartao(objeto, lugar),
      });
    });

    sceneManager.adicionar(this.grupo);
    input.conferirTamanhoDeAlvo(this.cartoes[0], `cartao da tarefa ${this.tarefa.id}`);
  }

  aoSoltarCartao(cartao, lugar) {
    const { audio } = this.ctx;

    if (cartao.userData.noLugar) {
      cartao.userData.noLugar.userData.ocupadoPor = null;
      cartao.userData.noLugar = null;
    }

    if (!lugar) {
      audio.tocarTentarDeNovo(); // D6
      return;
    }

    // Lugar ocupado: os dois cartoes trocam de lugar. Nunca "nao pode".
    const morador = lugar.userData.ocupadoPor;
    if (morador && morador !== cartao) {
      const vago = this.lugares.find((l) => !l.userData.ocupadoPor && l !== lugar);
      if (vago) {
        vago.userData.ocupadoPor = morador;
        morador.userData.noLugar = vago;
        morador.position.set(vago.position.x, vago.position.y + 0.13, vago.position.z);
      } else {
        morador.userData.noLugar = null;
        morador.position.z += 3.0;
      }
    }

    lugar.userData.ocupadoPor = cartao;
    cartao.userData.noLugar = lugar;
    cartao.position.set(lugar.position.x, lugar.position.y + 0.13, lugar.position.z);

    audio.tocarToque(lugar.userData.passo);
    if (this.concluiu()) audio.tocarSucesso();
  }

  concluiu() {
    return this.lugares.every((l) => l.userData.ocupadoPor?.userData.passo === l.userData.passo);
  }

  atualizar(dt) {
    for (const c of this.cartoes) {
      if (!c.userData.noLugar) c.rotation.y += dt * 0.5;
    }
  }

  async concluir() {
    return super.concluir({ concluiu: this.concluiu() });
  }

  descartar() {
    const { input, sceneManager } = this.ctx;
    for (const c of this.cartoes) input.desregistrar(c);
    if (this.grupo) {
      sceneManager.remover(this.grupo);
      this.grupo.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
    }
    this.lugares = [];
    this.cartoes = [];
    this.grupo = null;
    super.descartar();
  }
}

/** Sequencia certa, com N pares trocados de lugar. */
function comErrosPlantados(quantidade, erros) {
  const ordem = [...Array(quantidade).keys()];
  for (let i = 0; i < erros; i++) {
    const a = Math.floor(Math.random() * quantidade);
    let b = Math.floor(Math.random() * quantidade);
    if (a === b) b = (b + 1) % quantidade;
    [ordem[a], ordem[b]] = [ordem[b], ordem[a]];
  }
  return ordem;
}

function embaralhar(lista) {
  for (let i = lista.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [lista[i], lista[j]] = [lista[j], lista[i]];
  }
  return lista;
}
